import { Col, Form, Table, Tag, Typography, theme } from "antd";
import { useState } from "react";
import DynamicForm from "../../controls/DynamicForm";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import {
  getPlatformUsers,
  getRecommendedMaterials,
} from "../../repository/data.repository";
import {
  WellbeingMaterialRecommendation,
} from "../../repository/data.default";
import { InputObject } from "../../controls/default";
import { retry } from "../../utils/http";

const PAGE_SIZE = 20;

// Colour the Source tag by reach class: Public (default), Assigned/required (red/orange), Targeted: KPI (blue).
function sourceColor(source: string): string {
  if (source.startsWith("Assigned (required)")) return "red";
  if (source.startsWith("Assigned")) return "orange";
  if (source.startsWith("Targeted")) return "blue";
  return "default";
}

// Proves the reach model: pick a user → list every published material reachable to them + WHY (Source).
function MaterialTargetingPreview() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<{ readonly user?: InputObject }>([], form);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<WellbeingMaterialRecommendation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loadedUser, setLoadedUser] = useState<string | undefined>(undefined);

  // Server-paged: each page fetches only PAGE_SIZE materials, so a user reachable by hundreds of
  // materials never pulls the whole set into one response.
  function load(userUid: string, pageNumber: number): void {
    setLoading(true);
    retry(() =>
      getRecommendedMaterials(userUid, {
        searchQuery: { conditions: [] },
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    )
      .then((res) => {
        setRows(res.data);
        setTotal(res.totalRecords);
        setPage(pageNumber);
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
  }

  function onChange(): void {
    const userUid = form.getFieldValue("user")?.value;
    if (userUid === loadedUser) return;
    setLoadedUser(userUid);
    if (!userUid) {
      setRows([]);
      setTotal(0);
      setPage(0);
      return;
    }
    load(userUid, 0);
  }

  return (
    <Col className="flex flex-auto flex-col overflow-hidden">
      <DynamicForm
        className="w-72"
        form={form}
        schema={{
          groups: [
            {
              controls: [
                {
                  type: "combobox",
                  name: "user",
                  label: "User",
                  request: createSearchRequest(getPlatformUsers, "userName"),
                },
              ],
            },
          ],
        }}
        onChange={onChange}
      />
      <Table
        className="mt-2 flex-auto"
        size="small"
        rowKey={(r: WellbeingMaterialRecommendation) => r.uid}
        loading={loading}
        dataSource={rows}
        // Bounded body with a sticky header + client-side paging, so a user reachable by many
        // materials stays readable (header never scrolls away; ~20 rows per page).
        scroll={{ y: 300 }}
        pagination={{
          current: page + 1,
          pageSize: PAGE_SIZE,
          total,
          size: "small",
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: (p) => {
            const userUid = form.getFieldValue("user")?.value;
            if (userUid) load(userUid, p - 1);
          },
        }}
        locale={{
          emptyText: values?.user
            ? "No reachable materials for this user."
            : "Pick a user to preview reachable materials.",
        }}
        columns={[
          { title: "Material", dataIndex: "name", key: "name", width: 240 },
          {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 120,
          },
          {
            title: "Category",
            dataIndex: "categoryName",
            key: "categoryName",
            width: 160,
            render: (v: string) => v ?? "-",
          },
          {
            title: "Source",
            dataIndex: "source",
            key: "source",
            render: (source: string) => (
              <Tag color={sourceColor(source)}>{source}</Tag>
            ),
          },
        ]}
      />
      <Typography.Text
        className="mt-1 text-xs"
        style={{ color: token.colorTextDescription }}
      >
        Source = the strongest reason a material surfaced: Public, Assigned
        (required), Assigned, or Targeted: KPI band.
      </Typography.Text>
    </Col>
  );
}

export default MaterialTargetingPreview;
